import { createEffect, createSignal, For, Match, onCleanup, Show, Switch, type Component } from 'solid-js'
import { Dynamic } from 'solid-js/web'
import { SideNav } from '@ui/components/SideNav'
import { Button } from '@ui/components/Button'
import type { UsuarioPublico } from '@shared/types'
import type { InfoBanco } from '../nucleo/worker'
import { GRUPOS, MODULOS, type IdModulo } from './modulos'
import { Comandos, type Comando } from './Comandos'
import { StatusSync, AvisosSync } from './StatusSync'
import { sync } from '../nucleo/cliente'
import { versaoNovaPronta, atualizarAgora } from '../pwa'
import { Clientes } from '../telas/Clientes'
import { Catalogo } from '../telas/Catalogo'
import { Estoque } from '../telas/Estoque'
import { Importacoes } from '../telas/Importacoes'
import { Vendas } from '../telas/Vendas'
import { FuturasImportacoes } from '../telas/FuturasImportacoes'
import { Dashboard } from '../telas/Dashboard'
import { Configuracoes } from '../telas/Configuracoes'
import { Chat } from '../telas/chat/Chat'
import { iniciarAvisos, setConversaAberta } from '../telas/chat/avisos'
import { Pedidos } from '../telas/pedidos/Pedidos'
import { setDocAberto } from '../telas/docs/estado'

// Docs (F5): o editor só é carregado ao abrir o módulo. Sem <Suspense> de propósito:
// ele também pegaria as buscas da própria tela (a 1ª busca escondia a tela e o campo
// perdia o foco no meio da digitação — achado no teste).
const [Docs, setDocs] = createSignal<Component<{ usuario: UsuarioPublico }>>()
const carregarDocs = () => {
  if (!Docs()) void import('../telas/docs/Docs').then((m) => setDocs(() => m.default))
}
import { preferencia, setPreferencia, type PreferenciaTema } from '../tema/tema'

const NOMES_TEMA: Record<PreferenciaTema, string> = { claro: 'Claro', escuro: 'Escuro', sistema: 'Igual ao sistema' }

export function Casca(props: { usuario: UsuarioPublico; banco: InfoBanco; aoSair: () => void }) {
  const [atual, setAtual] = createSignal<IdModulo>('inicio')
  const [menuAberto, setMenuAberto] = createSignal(false)
  const ir = (id: IdModulo) => {
    setAtual(id)
    setMenuAberto(false)
  }
  const modulo = () => MODULOS.find((m) => m.id === atual())!
  // Chat (F4): contador de não lidas no menu/título e avisos do computador em qualquer tela
  const naoLidasChat = iniciarAvisos(() => props.usuario?.id ?? null) // usuario some no instante do logout
  // clique no aviso do computador: vai para o Chat, já na conversa
  const abrirConversa = (e: Event) => (setConversaAberta((e as CustomEvent<number>).detail), ir('chat'))
  window.addEventListener('werc:abrir-conversa', abrirConversa)
  onCleanup(() => window.removeEventListener('werc:abrir-conversa', abrirConversa))
  // link de documento no chat: vai para o Docs, já no documento
  const abrirDocumento = (e: Event) => (setDocAberto((e as CustomEvent<string>).detail), ir('docs'))
  window.addEventListener('werc:abrir-doc', abrirDocumento)
  onCleanup(() => window.removeEventListener('werc:abrir-doc', abrirDocumento))
  createEffect(() => atual() === 'docs' && carregarDocs())

  const comandos = (): Comando[] => [
    ...MODULOS.map((m) => ({
      id: `ir-${m.id}`,
      titulo: `Ir para ${m.nome}`,
      dica: m.pronto ? m.grupo : `chega na ${m.fase}`,
      desabilitado: !m.pronto,
      executar: () => ir(m.id),
    })),
    ...(['claro', 'escuro', 'sistema'] as PreferenciaTema[]).map((p) => ({
      id: `tema-${p}`,
      titulo: `Tema: ${NOMES_TEMA[p]}`,
      dica: preferencia() === p ? 'atual' : undefined,
      executar: () => setPreferencia(p),
    })),
    { id: 'sincronizar', titulo: 'Sincronizar agora', executar: () => void sync.agora() },
    { id: 'sair', titulo: 'Sair', executar: props.aoSair },
  ]

  return (
    <div class="h-dvh flex bg-page text-ink">
      {/* Navegação lateral (vira gaveta no celular) */}
      <div
        class="fixed inset-0 z-30 bg-black/30 md:hidden"
        classList={{ hidden: !menuAberto() }}
        onClick={() => setMenuAberto(false)}
      />
      <aside
        class="fixed md:static z-40 inset-y-0 left-0 w-[232px] shrink-0 border-r border-edge bg-panel flex flex-col transition-transform md:translate-x-0"
        classList={{ '-translate-x-full': !menuAberto() }}
      >
        <div class="h-14 flex items-center px-5 font-semibold">
          <span class="text-accent">Werc</span>&nbsp;Industrial
        </div>
        <SideNav class="w-full flex-1 pt-0">
          <For each={GRUPOS}>
            {(g) => (
              <SideNav.Group label={g}>
                <For each={MODULOS.filter((m) => m.grupo === g)}>
                  {(m) => (
                    <SideNav.Item active={atual() === m.id} disabled={!m.pronto} onSelect={() => ir(m.id)}>
                      {m.nome}
                      <Show when={m.id === 'chat' && naoLidasChat() > 0}>
                        <span class="ml-auto rounded-full bg-accent px-1.5 text-[10px] font-semibold text-white tabular-nums">{naoLidasChat()}</span>
                      </Show>
                      <Show when={!m.pronto}>
                        <span class="ml-1.5 text-[10px] text-ink-placeholder">{m.fase}</span>
                      </Show>
                    </SideNav.Item>
                  )}
                </For>
              </SideNav.Group>
            )}
          </For>
        </SideNav>
        <div class="p-4 border-t border-edge text-xs text-ink-subtle flex flex-col gap-1">
          <StatusBanco banco={props.banco} />
        </div>
      </aside>

      <div class="flex-1 min-w-0 flex flex-col">
        <header class="h-14 shrink-0 border-b border-edge bg-panel flex items-center gap-3 px-4">
          <Button variant="ghost" size="sm" class="md:hidden" onClick={() => setMenuAberto(true)} aria-label="Abrir menu">
            Menu
          </Button>
          <div class="text-sm text-ink-subtle truncate">{modulo().nome}</div>
          <div class="ml-auto flex items-center gap-2">
            <Show when={versaoNovaPronta()}>
              <Button variant="accent" size="sm" onClick={atualizarAgora} title="Uma versão nova do Werc foi baixada">
                Atualizar
              </Button>
            </Show>
            <StatusSync />
            <Comandos comandos={comandos} />
            <span class="hidden sm:inline text-sm text-ink-muted">{props.usuario.nome}</span>
            <Button variant="ghost" size="sm" onClick={props.aoSair}>
              Sair
            </Button>
          </div>
        </header>
        <AvisosSync aoEntrarDeNovo={props.aoSair} />
        <main class="flex-1 min-h-0 overflow-auto">
          <Switch>
            <Match when={atual() === 'inicio'}>
              <Inicio usuario={props.usuario} banco={props.banco} ir={ir} />
            </Match>
            <Match when={atual() === 'clientes'}>
              <Clientes podeEditar={props.usuario.perfil === 'ADMIN' || props.usuario.perfil === 'VENDEDOR'} />
            </Match>
            <Match when={atual() === 'catalogo'}>
              <Catalogo podeEditar={props.usuario.perfil === 'ADMIN' || props.usuario.perfil === 'ESTOQUE'} />
            </Match>
            <Match when={atual() === 'estoque'}>
              <Estoque podeEditar={props.usuario.perfil === 'ADMIN' || props.usuario.perfil === 'ESTOQUE'} />
            </Match>
            <Match when={atual() === 'importacoes'}>
              <Importacoes podeEditar={props.usuario.perfil === 'ADMIN' || props.usuario.perfil === 'ESTOQUE'} />
            </Match>
            <Match when={atual() === 'vendas'}>
              <Vendas podeEditar={props.usuario.perfil === 'ADMIN' || props.usuario.perfil === 'VENDEDOR'} />
            </Match>
            <Match when={atual() === 'futuras'}>
              <FuturasImportacoes podeEditar={props.usuario.perfil === 'ADMIN' || props.usuario.perfil === 'ESTOQUE'} />
            </Match>
            <Match when={atual() === 'dashboard'}>
              <Dashboard />
            </Match>
            <Match when={atual() === 'chat'}>
              <Chat usuario={props.usuario} />
            </Match>
            <Match when={atual() === 'docs'}>
              <Show when={Docs()} fallback={<div class="h-full flex items-center justify-center text-sm text-ink-subtle">Abrindo o Docs…</div>}>
                <Dynamic component={Docs()} usuario={props.usuario} />
              </Show>
            </Match>
            <Match when={atual() === 'pedidos'}>
              <Pedidos podeEditar={props.usuario.perfil === 'ADMIN' || props.usuario.perfil === 'VENDEDOR'} />
            </Match>
            <Match when={atual() === 'config'}>
              <Configuracoes banco={props.banco} usuario={props.usuario} />
            </Match>
          </Switch>
        </main>
      </div>
    </div>
  )
}

function StatusBanco(props: { banco: InfoBanco }) {
  return (
    <Show
      when={props.banco.persistente}
      fallback={<span class="text-warning">Banco temporário: os dados somem ao fechar</span>}
    >
      <span class="flex items-center gap-1.5">
        <span class="size-1.5 rounded-full bg-success" /> Banco local salvo neste computador
      </span>
    </Show>
  )
}

function Inicio(props: { usuario: UsuarioPublico; banco: InfoBanco; ir: (id: IdModulo) => void }) {
  return (
    <div class="p-6 flex flex-col gap-6 max-w-3xl">
      <div>
        <h1 class="text-2xl font-semibold">Olá, {props.usuario.nome.split(' ')[0]}</h1>
        <p class="text-sm text-ink-subtle mt-1">
          Esta é a Werc 2.0 em construção. Os módulos marcados com a fase chegam nas próximas etapas; até lá, o
          sistema atual continua atendendo normalmente.
        </p>
      </div>
      <Show when={props.banco.aviso}>
        <div class="rounded-xl border border-warning/40 bg-warning/10 p-4 text-sm">{props.banco.aviso}</div>
      </Show>
      <div class="grid sm:grid-cols-2 gap-3">
        <For each={MODULOS.filter((m) => m.id !== 'inicio')}>
          {(m) => (
            <button
              type="button"
              disabled={!m.pronto}
              onClick={() => props.ir(m.id)}
              class="text-left rounded-xl border border-edge bg-panel p-4 hover:bg-hover disabled:opacity-60 disabled:cursor-default"
            >
              <div class="font-medium">{m.nome}</div>
              <div class="text-xs text-ink-subtle mt-0.5">{m.pronto ? m.grupo : `Chega na ${m.fase}`}</div>
            </button>
          )}
        </For>
      </div>
    </div>
  )
}
